import { type ComponentTypes } from '@lib/apps/canvas-builder/types/canvas'
import { type OrganizationSettings } from '@lib/types/organizationSettings.types'

export enum CanvasExcludedComponentTypesEnum {
  Root = 'Root',
  GridItem = 'GridItem',
  File = 'File',
  Slide = 'Slide',
}

export type CanvasExcludedComponentTypes = keyof typeof CanvasExcludedComponentTypesEnum
export type CanvasComponentTypes = Exclude<keyof typeof ComponentTypes, CanvasExcludedComponentTypes>

export type CanvasComponentAvailability = {
  [component in CanvasComponentTypes]: boolean
}

export type CanvasComponentAvailabilityRoles = {
  [component in CanvasComponentTypes]: {
    admin?: boolean
    rep?: boolean
  }
}

export type AutohideSectionOnCreated = {
  autohide_in_shared_link?: string
  start_date?: Date
}

export type InstanceSettings = OrganizationSettings & {
  canvas?: {
    hide_page_break_in_toc?: boolean // Hide PageBreak in ToC
    disable_display_index_in_toc?: boolean // Disable display index in ToC
    autohide_section_on_created?: AutohideSectionOnCreated // apps setting (used in PRH for now)
    component_availability?: CanvasComponentAvailability & { apps?: Record<string, boolean> } // which components are available for the reps to add
    component_availability_roles?: CanvasComponentAvailabilityRoles & {
      apps?: Record<string, { admin?: boolean; rep?: boolean }>
    } // which components are available for admins and reps separately
    content_grid_autofill_files_filter_iteratee?: string // TO BE DEPRECATED: control filtering via an eval() iteratee
    section_content_grid_autofill_files_filter_iteratee?: string // TO BE DEPRECATED: control filtering via an eval() iteratee
    show_extra_exit_button_in_fullscreen_mode?: boolean // show an extra exit button at the top left in fullscreen mode
    show_fallback_section_name_on_collapsibles?: boolean // needed when header is not autofilled by an AI app
    capture_component_enter_exit_events?: 'all' | 'named_components' // tracking intensity
    enable_collection_player_component?: boolean // Allow to use CollectionPlayer canvas component
    autoselect_selected_canvas_account?: boolean // autoselect the account in the call bar when a canvas is opened
    require_account_on_canvas_creation?: boolean // require an account to be selected when creating a canvas
    enable_canvases_account_filtering?: boolean // filter saved canvases by selected account when available
    pptx_slide_image_width?: number // width used during slide image generation from a PPTX (default 1160)
    correlation_id_cleanup_delay?: number // delay in ms to cleanup canvas correlation id
    async_download_expiration_hours?: number // expiration time in hours for async download URLs (default 24 hours)
    async_download_api_url?: string // URL for the async download API (default: https://downloader-test.my.pitcher.com/prod/convert)
    async_download_callback_url?: string // URL for the async download callback (default: https://download-coordinator.fly.dev/callback)
    section_list_mode?: 'pagination' | 'collapsibles' // defines the display mode of section list (default: pagination)
    disable_rep_canvas_creation?: boolean // toggles the ability to create Rep Canvas (default: false)
    disable_shared_link_creation?: boolean // toggles the ability to create Shared Links for Canvases (default: false)
    hide_meeting_bar?: boolean // flag toggles the visibility of the Meeting Bar (default: false)
  }
  custom_event?: string //control custom event object, create its query, get its field mappings via an eval()
  enable_recents?: boolean // enable recents section in the browser/files/content selector
  enable_favorites?: boolean // enable favorites section in the browser/files/content selector
  enable_multipeer_connectivity?: boolean // enable multipeer connectivity features
  enable_syncbox?: boolean // enable syncbox features
  enable_sync_file_filtering?: boolean // enable sync file filtering features in canvas content for reps
  enable_sfdc_sync?: boolean // enable SFDC sync features (SmartStore queries on iOS)
  time_format?: string // time format string (e.g., 'HH:mm' for 24-hour, 'h:mm A' for 12-hour with AM/PM). Default: 'HH:mm'
  date_format?: string // date format string (e.g., 'YYYY-MM-DD', 'MMM D, YY'). Default: 'YYYY-MM-DD'
  is_file_expiration_mandatory?: boolean // when enabled, files cannot have expiration date set to "never"
  ios?: InstanceSettings // iOS app specific settings

  // export type IOSOverrides = InstanceSettings & {
  // iPadMini?: IPadMiniOverrides // iPad Mini specific settings
  // }

  // export type IPadMiniOverrides = InstanceSettings
}
